// App.tsx

import * as React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
  Location,
} from "react-router-dom";
import HomePage from "./pages/HomePage/HomePage";
import CustomFormPage from "./pages/form/CustomFormPage";
import RecordDetailPage from "./pages/RecordDetail/RecordDetailPage";
import CustomSignInPage from "./pages/login/CustomSignInPage";
import ProgressInquiry from "./pages/ProgressInquiry/ProgressInquiry";
import ReviewManagement from "./pages/ReviewManagement/ReviewManagement";
import ApplicationAudit from "./pages/ApplicationAudit/ApplicationAudit";
import AwaitingReport from "./pages/AwaitingReport/AwaitingReport";
import WorkFlowPage from "./pages/EditWorkFlow/WorkFlowPage";
import EditWorkFlow from "./pages/EditWorkFlow/EditWorkFlow";
import SearchWorkflowPage from "./pages/searchWorkflow/SearchWorkflowPage";
import OperationCompleted from "./pages/OperationCompleted/OperationCompleted";
import { setLogoutHandler } from "./service/AxiosService";
import { UserProvider, useUser } from "./context/UserContext";
import { ChangePasswordForm } from "./pages/ChangePasswordForm/ChangePasswordForm";
import MyPage from "./pages/MyPage/MyPage";
import { ScrollToTop } from "./components/Scroll/ScrollToTop";
import { BackToTopButton } from "./components/Scroll/BackToTopButton";
import UserManagement from "./pages/UserManagement/UserManagement";
import GroupManagement from "./pages/GroupManagement/GroupManagement";
import MailTemplatePage from "./pages/MailTemplate/MailTemplatePage";
import SsoLoginCallbackPage from "./pages/login/SsoLoginCallbackPage";
import ApplicationTypeManagement from "./pages/ApplicationTypeManagement/ApplicationTypeManagement";
import ThemeManagement from "./pages/ThemeManagement/ThemeManagement";
import NewsManagement from "./pages/NewsManagement/NewsManagement";

/**
 * アプリケーション初期化コンポーネント
 * - AxiosServiceのログアウトハンドラーをログイン画面遷移処理に設定する
 */
const AppInitializer: React.FC = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    setLogoutHandler(() => {
      navigate("/login");
    });
  }, [navigate]);
  return null;
};

/**
 * 権限チェックの行うためのRoutes
 */
const CustomRoutes: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [customLocation, setCustomLocation] = React.useState<
    Location | undefined
  >();
  const { user } = useUser();

  const getPath = (path: string) =>
    path.split("/").slice(0, 2).join("/").toLowerCase();

  React.useEffect(() => {
    const userAuthorities = user?.authorities ?? [];

    // 遷移先画面を開く際にユーザーの権限が十分かチェックする
    const hasAuth = (() => {
      switch (getPath(location.pathname)) {
        case getPath("/login"):
        case getPath("/callback"):
        case getPath("/MyPage"):
        case getPath("/ChangePasswordForm"):
          return true;
        case getPath("/"):
        case getPath("/ProgressInquiry"):
        case getPath("/detail/:id?"):
        case getPath("/SearchWorkflow"):
        case getPath("/OperationCompleted"):
        case getPath("/AwaitingReport"):
          // case getPath("/Test"):
          // case getPath("/CustomSignInPage"):
          return userAuthorities.includes("view_requests");
        case getPath("/ReviewManagement"):
          return userAuthorities.includes("approve_or_reject_requests");
        case getPath("/CustomForm/:id"):
          return userAuthorities.includes("create_requests");
        case getPath("/ApplicationAudit"):
          return userAuthorities.includes("view_out_of_scope_requests");
        case getPath("/WorkFlowPage"):
        case getPath("/edit/:id"):
        case getPath("/MailTemplatePage"):
          return userAuthorities.includes("manage_workflow");
        case getPath("/UserManagement"):
        case getPath("/GroupManagement"):
          return userAuthorities.includes("manage_users");
        case getPath("/ApplicationTypeManagement"):
        case getPath("/ThemeManagement"):
          return userAuthorities.includes("manage_workflow");
        case getPath("/NewsManagement"):
          return userAuthorities.includes("manage_dashboard");
        default:
          console.log("権限設定がありません。path: " + location.pathname);
          return true; // 権限設定がない画面はチェック不要
      }
    })();

    if (hasAuth) {
      setCustomLocation(location); // 権限がある場合locationを更新
    } else {
      alert("ユーザーはこの画面を表示する権限がありません。");
    }
  }, [location, user?.authorities]);

  return <Routes location={customLocation}>{children}</Routes>;
};

const App: React.FC = () => {
  return (
    <>
    <Router>
      <ScrollToTop />
      <AppInitializer />
      <UserProvider>
        <CustomRoutes>
          {/* ログインページ */}
          <Route path="/login" element={<CustomSignInPage />} />
          <Route path="/callback" element={<SsoLoginCallbackPage />} />
          {/* ホームページ */}
          <Route path="/" element={<HomePage />} />
          {/* 申請中申請一覧ページ */}
          <Route path="/ProgressInquiry" element={<ProgressInquiry />} />
          <Route path="/detail/:id?" element={<RecordDetailPage />} />
          {/* 承認待ち申請ページ */}
          <Route path="/ReviewManagement" element={<ReviewManagement />} />
          {/* 応答中申請一覧ページ */}
          <Route path="/AwaitingReport" element={<AwaitingReport />} />
          {/* 全申請一覧ページ */}
          <Route path="/ApplicationAudit" element={<ApplicationAudit />} />
          {/* ワークフロー一覧・新規作成・編集ページ */}
          <Route path="/WorkFlowPage" element={<WorkFlowPage />} />
          {/* ワークフロー編集ページ */}
          <Route path="/edit/:id" element={<EditWorkFlow />} />
          <Route path="/MailTemplatePage" element={<MailTemplatePage />} />
          {/* 申請フォームページ */}
          <Route path="/CustomForm/:id" element={<CustomFormPage />} />
          <Route path="/SearchWorkflow" element={<SearchWorkflowPage />} />
          {/* 操作完了ページ */}
          <Route path="/OperationCompleted" element={<OperationCompleted />} />
          {/* パスワード変更ページ */}
          <Route path="/ChangePasswordForm" element={<ChangePasswordForm />} />
          <Route path="/MyPage" element={<MyPage />} />
          {/* ユーザー新規作成・編集ページ */}
          <Route path="/UserManagement" element={<UserManagement />} />
          {/* 組織図ページ */}
          <Route path="/GroupManagement" element={<GroupManagement />} />
          {/* 申請種別管理ページ */}
          <Route path="/ApplicationTypeManagement" element={<ApplicationTypeManagement />} />
          {/* テーマ管理ページ */}
          <Route path="/ThemeManagement" element={<ThemeManagement />} />
          {/* ニュース管理ページ */}
          <Route path="/NewsManagement" element={<NewsManagement />} />
        </CustomRoutes>
      </UserProvider>
    </Router>

    {/* 「上に戻る」ボタン */}
      <BackToTopButton />
    </>
  );
};

export default App;
